import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pill, Plus } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { get } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { dateTime, timeOnly } from '@/lib/utils';
import { useRealtimeInvalidate } from '@/lib/realtime';
import { DataTable } from '@/components/ui/layout';
import { Badge, Button, StatusBadge } from '@/components/ui/primitives';
import { ShellSectionHeading } from '@/components/clinical-shell/ShellPanel';
import { doseCanBeRecorded } from '../../dose-timing';
import { admissionIsOpen, type Admission, type Dose } from '../../admission/types';
import { doseOverdue, useMinuteClock } from '../emar-clock';
import { AdministerDialog } from './AdministerDialog';

export function emarKey(admissionId: string) {
  return ['emar', admissionId] as const;
}

export function useEmar(admissionId: string, enabled: boolean) {
  useRealtimeInvalidate(enabled ? [`emar:${admissionId}`] : [], null, [emarKey(admissionId)]);
  return useQuery({
    queryKey: emarKey(admissionId),
    queryFn: () => get<Dose[]>(`/ipd/admissions/${admissionId}/emar?windowHours=24`),
    enabled,
  });
}

export function EmarSection({ admission, newOrderHref }: { admission: Admission; newOrderHref: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [administering, setAdministering] = useState<Dose | null>(null);
  const current = admissionIsOpen(admission);
  const emar = useEmar(admission.id, true);
  const now = useMinuteClock();

  return (
    <>
      <ShellSectionHeading
        icon={Pill}
        title={tr('ipd', 'ws_emr_emar')}
        description={tr('ipd', 'ws_emar_desc')}
        actions={
          current && can('cpoe.write') ? (
            <Link to={newOrderHref}>
              <Button size="sm" variant="outline">
                <Plus className="h-4 w-4" aria-hidden /> {tr('ipd', 'new_order')}
              </Button>
            </Link>
          ) : null
        }
      />
      <DataTable
        rows={emar.data}
        loading={emar.isLoading}
        refreshing={emar.isFetching}
        error={emar.error}
        rowKey={(row) => row.id}
        empty={tr('ipd', 'no_doses_scheduled_in_this_window')}
        columns={[
          {
            header: tr('ipd', 'due'),
            width: '110px',
            cell: (row) => <span className={doseOverdue(row, now) ? 'font-semibold text-destructive' : 'tabular'}>{timeOnly(row.scheduledAt)}</span>,
          },
          {
            header: tr('ipd', 'medicine'),
            cell: (row) => (
              <div>
                <p className="font-medium">
                  {row.order.label}
                  {row.order.isHighAlert && (
                    <Badge variant="destructive" className="ms-1">
                      {tr('ipd', 'high_alert')}
                    </Badge>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">{[row.order.dose, row.order.route, row.order.frequency].filter(Boolean).join(' · ')}</p>
              </div>
            ),
          },
          { header: tr('ipd', 'status'), cell: (row) => <StatusBadge status={doseOverdue(row, now) ? 'overdue' : row.status} /> },
          { header: tr('ipd', 'given_at'), cell: (row) => (row.administeredAt ? dateTime(row.administeredAt) : '—') },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              doseCanBeRecorded(row, current) &&
              can('emar.administer') && (
                <Button size="sm" onClick={() => setAdministering(row)}>
                  {tr('ipd', 'record')}
                </Button>
              ),
          },
        ]}
      />
      {administering !== null && (
        <AdministerDialog
          dose={administering}
          onClose={() => setAdministering(null)}
          onSaved={() => {
            setAdministering(null);
            void queryClient.invalidateQueries({ queryKey: emarKey(admission.id) });
          }}
        />
      )}
    </>
  );
}
